/**
 * The in-flight `ESC` menu as a `SurfaceController` (G38).
 *
 * It is the smallest surface in the shell and deliberately so: **a dialog and
 * nothing else.** `screen()` returns a chrome-less `ShellScreen` with no
 * widgets, so the only thing painted is the board, over a transparent frame,
 * over the frozen cockpit — which is how the original draws it
 * (`docs/game/ui-reference/dialog-mission-not-complete.png`).
 *
 * Nothing here decides *whether* the board may be shown; `escMenuDialog` does,
 * from the situation the route hands it every time it is opened. This holds the
 * focus and turns a row into one of the route's four callbacks, and that is all.
 * The route owns pause, the sim and the navigation, because the route is what
 * owns the sortie.
 */

import { escMenuDialog, type EscMenuSituation } from "./model/esc-menu-dialog.ts";
import type { MenuAction } from "./model/menu-model.ts";
import type { ShellDialog, ShellScreen } from "./model/shell-screen.ts";
import type { SurfaceController } from "./screen-runtime.ts";

export interface EscMenuSurfaceOptions {
  /** Read at paint time, not at construction — the score ticks and the guards change while the board is up. */
  readonly situation: () => EscMenuSituation;
  /** CONTINUE FLYING, and `Escape` again. Puts the player back in the aeroplane. */
  readonly onContinue: () => void;
  /** RETURN TO BASE — end the sortie and go to the debriefing. */
  readonly onReturnToBase: () => void;
  /** RESTART MISSION — fly the identical sortie again. */
  readonly onRestart: () => void;
  /** BAIL OUT — the same engine command the held `H` fires, never a second copy of it. */
  readonly onBailOut: () => void;
}

/**
 * The frame the board floats on: no board chrome, no panel, no background, no
 * widgets. `cancel` is CONTINUE FLYING, so `Escape` closes the menu the same way
 * the top row does — `screen-runtime.ts` already routes `Escape` to a dialog's
 * dismissal first and to this second, which means the key that opens the board
 * also shuts it without anything here having to say so.
 */
const ESC_MENU_SCREEN: ShellScreen = {
  id: "fly/esc-menu",
  chrome: { kind: "none" },
  widgets: [],
  cancel: { kind: "close" },
};

export function createEscMenuSurface(options: EscMenuSurfaceOptions): SurfaceController {
  let focus = 0;
  let dialog: ShellDialog | undefined;

  function current(): ShellDialog {
    // Rebuilt from the live situation on every read, so the SCORE counts up
    // while the board is open and a row that stops being available (the pilot
    // hits the silk from the dialog, say) stops being drawn. The focus index is
    // carried across, clamped, because the row count can change under it.
    const next = escMenuDialog(options.situation());
    const rows = Math.max(1, next.rows.length);
    focus = Math.min(focus, rows - 1);
    dialog = { ...next, focus };
    return dialog;
  }

  return {
    // Fonts and the palette only: this surface paints no artwork of its own.
    assets: { images: [], sprites: [] },
    screen: () => ESC_MENU_SCREEN,
    dialog: () => current(),
    focus: () => focus,
    setFocus(index) {
      focus = index;
    },
    setDialog(next) {
      // The runtime moves focus by handing back a whole dialog; only the index
      // is kept, because the board itself is derived and not stored.
      if (next) focus = next.focus;
      else options.onContinue();
    },
    typeKey: () => false,
    run(action: MenuAction | undefined): string | undefined {
      if (!action) return undefined;
      if (action.kind === "close") {
        options.onContinue();
        return undefined;
      }
      if (action.kind !== "command") return undefined;
      if (action.command === "esc:return") options.onReturnToBase();
      if (action.command === "esc:restart") options.onRestart();
      if (action.command === "esc:bail") options.onBailOut();
      return undefined;
    },
  };
}
